import { useState } from "react";
import { Layout } from "@/components/Layout";
import { useUsersQuery } from "@/hooks/useUsers";
import { useAuth } from "@/hooks/useAuth";
import type { UserWithRole, UserRole } from "@/types/users";
import { ROLE_LABELS, ROLE_COLORS } from "@/types/users";
import { InviteUserDialog } from "@/components/users/InviteUserDialog";
import { EditRoleDialog } from "@/components/users/EditRoleDialog";
import { DeleteUserDialog } from "@/components/users/DeleteUserDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { UserPlus, MoreHorizontal, Pencil, Trash2, Search, CheckCircle2, Clock } from "lucide-react";

export default function Users() {
  const { user: currentUser } = useAuth();
  const { data: users = [], isLoading } = useUsersQuery();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<UserRole | "all">("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserWithRole | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserWithRole | null>(null);

  // Suodata käyttäjät
  const filteredUsers = users.filter((user) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      !search ||
      user.email.toLowerCase().includes(searchLower) ||
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.partner_name?.toLowerCase().includes(searchLower);

    const matchesRole = roleFilter === "all" || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const getUserDisplayName = (user: UserWithRole) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name || "-";
  };

  return (
    <Layout>
      <div className="container py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Käyttäjät</h1>
            <p className="text-muted-foreground">Hallitse järjestelmän käyttäjiä ja rooleja</p>
          </div>
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Kutsu käyttäjä
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Käyttäjälista</CardTitle>
            <CardDescription>
              Yhteensä {users.length} käyttäjää
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Hae nimellä tai sähköpostilla..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={roleFilter}
                onValueChange={(value) => setRoleFilter(value as UserRole | "all")}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Kaikki roolit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Kaikki roolit</SelectItem>
                  {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nimi</TableHead>
                      <TableHead>Sähköposti</TableHead>
                      <TableHead>Rooli</TableHead>
                      <TableHead>Organisaatio</TableHead>
                      <TableHead>Tila</TableHead>
                      <TableHead>Luotu</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          {search || roleFilter !== "all"
                            ? "Ei hakutuloksia"
                            : "Ei käyttäjiä"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            {getUserDisplayName(user)}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.role ? (
                              <Badge className={ROLE_COLORS[user.role]}>
                                {ROLE_LABELS[user.role]}
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Ei roolia</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {user.partner_name || "-"}
                          </TableCell>
                          <TableCell>
                            {user.email_confirmed_at ? (
                              <div className="flex items-center gap-1 text-primary">
                                <CheckCircle2 className="h-4 w-4" />
                                <span className="text-sm">Aktiivinen</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                <span className="text-sm">Odottaa</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {format(new Date(user.created_at), "d.M.yyyy", { locale: fi })}
                          </TableCell>
                          <TableCell>
                            {user.id !== currentUser?.id && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Toiminnot</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => setEditUser(user)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Muokkaa roolia
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => setDeleteUser(user)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Poista
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialogs */}
        <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
        <EditRoleDialog user={editUser} open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)} />
        <DeleteUserDialog user={deleteUser} open={!!deleteUser} onOpenChange={(open) => !open && setDeleteUser(null)} />
      </div>
    </Layout>
  );
}
